/**
 * metaAttribution — persistent client-side capture of Meta Pixel signals so
 * server-side CAPI Purchase events have high Event Match Quality (EMQ).
 *
 * Meta uses these signals to re-attribute conversions to ad clicks on iOS
 * 14.5+ where the Pixel cookie alone is unreliable. Match-rate typically
 * jumps from ~50% (email-only) to ~85-95% (email + fbp + fbc + IP + UA).
 *
 * Stored keys (localStorage):
 *   opik_fbp     — _fbp cookie value (from Meta Pixel bootstrap)
 *   opik_fbc     — fb.{subdomain}.{ts}.{fbclid} click identifier
 *
 * Public helpers:
 *   captureMetaAttribution() — call once on app boot
 *   getMetaAttribution()     — { fbp, fbc } for sending with order forms
 */

const FBP_KEY = "opik_fbp";
const FBC_KEY = "opik_fbc";

const readCookie = (name) => {
  try {
    const m = document.cookie.match(
      new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)")
    );
    return m ? decodeURIComponent(m[1]) : "";
  } catch {
    return "";
  }
};

const writeCookie = (name, value, days = 90) => {
  try {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    // SameSite=Lax so it accompanies first-party CAPI requests
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/;SameSite=Lax`;
  } catch { /* ignore */ }
};

export const captureMetaAttribution = () => {
  if (typeof window === "undefined") return;
  try {
    // ----- _fbp (Pixel session) -----
    let fbp = readCookie("_fbp") || localStorage.getItem(FBP_KEY) || "";
    if (fbp && !readCookie("_fbp")) {
      // Reset the cookie in case it was cleared but we still had it cached
      writeCookie("_fbp", fbp);
    }
    if (fbp) localStorage.setItem(FBP_KEY, fbp);

    // ----- _fbc / fbclid (Click ID) -----
    let fbc = readCookie("_fbc") || localStorage.getItem(FBC_KEY) || "";
    const sp = new URLSearchParams(window.location.search);
    const fbclid = sp.get("fbclid");
    if (fbclid) {
      // Per Meta's spec:  fb.{subdomain}.{ts}.{fbclid}
      // We use subdomain index 1 (www-style domains). Meta's docs accept this.
      fbc = `fb.1.${Date.now()}.${fbclid}`;
      writeCookie("_fbc", fbc);
      localStorage.setItem(FBC_KEY, fbc);
    } else if (fbc && !readCookie("_fbc")) {
      writeCookie("_fbc", fbc);
    }
  } catch { /* never break app boot on telemetry */ }
};

export const getMetaAttribution = () => {
  if (typeof window === "undefined") return {};
  try {
    return {
      fbp: readCookie("_fbp") || localStorage.getItem(FBP_KEY) || undefined,
      fbc: readCookie("_fbc") || localStorage.getItem(FBC_KEY) || undefined,
    };
  } catch {
    return {};
  }
};
